import { LoadingButton } from "@mui/lab";
import {
    Alert,
    Box,
    Divider,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import * as React from "react";
import { ChangeEvent, useContext, useState } from "react";
import { useDispatch } from "react-redux";
import { ErrorDetailContainer } from "../../../api/config";
import { FolderAccessType, FolderDto, FolderService } from "../../../api/psc";
import { DialogContainer, DialogHeader } from "../../../components/exports";
import { FolderColors } from "../../../constants/ProjectConstants";
import { DialogContext } from "../../../contexts/DialogContext";
import { SnackbarContext } from "../../../contexts/SnackbarContext";
import { addOrUpdateFolderAction } from "../../../slices/SharedActions";
import { validateFolderName } from "../../../validations/FolderValidation";
import FolderColorItem from "../components/FolderColorItem";

export default function UpsertFolder(props: { folder?: FolderDto }) {
    const dispatch = useDispatch();

    const initialFolderState = props.folder ?? {
        title: "",
        accessType: FolderAccessType.PUBLIC,
        color: FolderColors[0],
    };

    const [folder, setFolder] = useState<FolderDto>(initialFolderState);
    const [requestInProgress, setRequestInProgress] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [accessType, setAccessType] = useState(
        props.folder?.accessType ?? FolderAccessType.PUBLIC
    );

    const { closeDialog } = useContext(DialogContext);
    const { showSnackbar } = useContext(SnackbarContext);

    const addOrUpdate = () => {
        const validationError = validateFolderName(folder);

        if (validationError) {
            setError(validationError);
            return;
        }

        setRequestInProgress(true);

        setError(null);

        const promise = !props.folder
            ? FolderService.createFolder("1", {
                  color: folder.color,
                  title: folder.title,
                  accessType,
              })
            : FolderService.updateFolder(folder.id, "1", {
                  color: folder.color,
                  title: folder.title,
                  accessType,
              });

        promise
            .then((r) => {
                dispatch(addOrUpdateFolderAction(r));

                closeDialog(true);

                if (props.folder) {
                    showSnackbar("پوشه با موفقت به روز شد", "success");
                } else {
                    showSnackbar("پوشه با موفقیت ایجاد شد", "success");
                }
            })
            .finally(() => {
                setRequestInProgress(false);
            })
            .catch((e: ErrorDetailContainer) => {
                showSnackbar(e.getFirstError(), "error");
            });
    };

    const onFolderTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const updatedFolder = { ...folder, title: event.target.value };
        setFolder(updatedFolder);
    };

    const onFolderColorChange = (color: string) => {
        const updatedFolder = { ...folder, color: color };
        setFolder(updatedFolder);
    };

    const handleAccessTypeChange = (
        event: React.MouseEvent<HTMLElement>,
        accessType: FolderAccessType | null
    ) => {
        if (accessType === null) {
            return;
        }
        setAccessType(accessType);
    };

    return (
        <DialogContainer>
            <DialogHeader title="ایجاد / ویرایش پوشه" />

            <Box sx={{ p: 2 }}>
                <TextField
                    fullWidth
                    sx={{ mb: 2 }}
                    label="نام پوشه"
                    variant="outlined"
                    value={folder.title}
                    onChange={onFolderTitleChange}
                />

                <ToggleButtonGroup
                    fullWidth
                    sx={{ mb: 2 }}
                    color="primary"
                    value={accessType}
                    exclusive
                    onChange={handleAccessTypeChange}
                >
                    <ToggleButton
                        color="success"
                        value={FolderAccessType.PUBLIC}
                    >
                        عمومی
                    </ToggleButton>
                    <ToggleButton
                        color="warning"
                        value={FolderAccessType.PRIVATE}
                    >
                        اختصاصی
                    </ToggleButton>
                </ToggleButtonGroup>

                {accessType === FolderAccessType.PUBLIC && (
                    <Alert sx={{ mb: 2 }} severity="info">
                        پوشه های عمومی برای دیگر کاربران حساب شما نیز فایل
                        مشاهده است.
                    </Alert>
                )}

                {accessType === FolderAccessType.PRIVATE && (
                    <Alert sx={{ mb: 2 }} severity="info">
                        پوشه های خصوصی تنها برای شما و صاحب حساب کاربری فابل
                        مشاهده است.
                    </Alert>
                )}

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mt: 2,
                        mb: 2,
                    }}
                >
                    {FolderColors.map((c) => (
                        <FolderColorItem
                            key={c}
                            color={c}
                            selected={folder.color === c}
                            onSelected={onFolderColorChange}
                        />
                    ))}
                </Box>

                <Divider sx={{ mb: 2 }} />

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <LoadingButton
                    fullWidth
                    variant="contained"
                    loading={requestInProgress}
                    onClick={addOrUpdate}
                >
                    ذخیره
                </LoadingButton>
            </Box>
        </DialogContainer>
    );
}
